import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Flex,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  Badge,
  Spinner,
  Select,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Portal,
  CloseButton,
  VStack,
  HStack,
  Tooltip,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  ArrowBackIcon, 
  DeleteIcon, 
  ViewIcon, 
  LockIcon,
  StarIcon
} from '@chakra-ui/icons';
import AuthContext from '../context/AuthContext';
import { adminService, sessionService } from '../services/api';
import { useAppToast } from '../utils/toast';

function Admin() {
  const { token, user } = useContext(AuthContext);
  const { showErrorToast, showSuccessToast } = useAppToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  // AI Model tab
  const [models, setModels] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [defaultModel, setDefaultModel] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [savingModel, setSavingModel] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  
  // Modal for session deletion confirmation
  const { 
    isOpen: isDeleteConfirmOpen, 
    onOpen: onOpenDeleteConfirm, 
    onClose: onCloseDeleteConfirm 
  } = useDisclosure();
  
  // Modal for password reset result
  const { 
    isOpen: isPasswordResetOpen, 
    onOpen: onOpenPasswordReset, 
    onClose: onClosePasswordReset 
  } = useDisclosure();

  useEffect(() => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      navigate('/');
      showErrorToast('Access denied', 'Only administrators can access this page');
      return;
    }

    // Fetch initial data based on active tab
    fetchData();
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [token, user, tabIndex]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (tabIndex === 0) { // Users tab
        const data = await adminService.getUsers();
        setUsers(data);
      } else if (tabIndex === 1) { // Sessions tab
        const data = await sessionService.getAllAdminSessions();
        setSessions(data);
      } else if (tabIndex === 2) { // AI Model tab
        const data = await adminService.getLlmModels();
        setModels(data.available || []);
        setActiveModel(data.active || null);
        setDefaultModel(data.default || null);
        setSelectedModel(data.active || '');
      }
    } catch (err) {
      setError(`Error fetching data: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (userId) => {
    try {
      setIsLoading(true);
      const data = await adminService.resetUserPassword(userId);

      setResetPasswordResult({
        userId,
        resetToken: data.resetToken,
        expiresAt: data.expiresAt,
        username: users.find(user => user._id === userId)?.username || 'User'
      });

      onOpenPasswordReset();

      // Auto-dismiss so the token doesn't sit on screen forever.
      timerRef.current = setTimeout(() => {
        onClosePasswordReset();
        setResetPasswordResult(null);
      }, 60000);
    } catch (err) {
      showErrorToast('Error resetting password', err.response?.data?.error || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = (sessionId) => {
    setSelectedSessionId(sessionId);
    onOpenDeleteConfirm();
  };

  const confirmDeleteSession = async () => {
    if (!selectedSessionId) return;

    try {
      await sessionService.deleteSession(selectedSessionId);

      // Update sessions list after delete
      setSessions(sessions.filter(session => session._id !== selectedSessionId));

      showSuccessToast('Session deleted', 'The session has been deleted successfully');
    } catch (err) {
      showErrorToast('Error deleting session', err.response?.data?.error || err.message);
    } finally {
      onCloseDeleteConfirm();
      setSelectedSessionId(null);
    }
  };

  const saveModel = async () => {
    if (!selectedModel) return;
    try {
      setSavingModel(true);
      const data = await adminService.setActiveLlmModel(selectedModel);
      setActiveModel(data.active);
      showSuccessToast('Model updated', `Chat backend now uses ${data.active}`);
    } catch (err) {
      showErrorToast('Error updating model', err.response?.data?.error || err.message);
    } finally {
      setSavingModel(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Colors
  const tableBgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBgColor = useColorModeValue('gray.50', 'gray.800');

  return (
    <Box bg={useColorModeValue('gray.50', 'gray.900')} minH="calc(100vh - 180px)" py={8}>
      <Container maxW="container.xl">
        <Flex mb={6} justifyContent="space-between" alignItems="center">
          <Button
            as={RouterLink}
            to="/"
            leftIcon={<ArrowBackIcon />}
            colorScheme="brand"
            variant="outline"
          >
            Back to Dashboard
          </Button>
          
          <Heading size="lg" textAlign="center">
            Admin Dashboard
          </Heading>
          
          <Box w={100} /> {/* Spacer for alignment */}
        </Flex>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs 
          colorScheme="brand" 
          onChange={(index) => setTabIndex(index)}
          variant="enclosed"
          mb={4}
        >
          <TabList>
            <Tab fontWeight="medium">Users</Tab>
            <Tab fontWeight="medium">Sessions</Tab>
            <Tab fontWeight="medium">AI Model</Tab>
          </TabList>
          <TabPanels>
            {/* Users Tab Panel */}
            <TabPanel p={0} pt={4}>
              {isLoading ? (
                <Flex justify="center" p={8}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              ) : (
                <Box 
                  borderRadius="lg"
                  boxShadow="md"
                  bg={tableBgColor}
                  overflowX="auto"
                >
                  <Table variant="simple">
                    <Thead bg={headerBgColor}>
                      <Tr>
                        <Th>Username</Th>
                        <Th>Role</Th>
                        <Th>Created At</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {users.map(user => (
                        <Tr key={user._id}>
                          <Td>{user.username}</Td>
                          <Td>
                            <Badge colorScheme={user.role === 'admin' ? 'red' : 'blue'}>
                              {user.role}
                            </Badge>
                          </Td>
                          <Td>{formatDate(user.createdAt)}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="brand"
                              leftIcon={<LockIcon />}
                              onClick={() => resetPassword(user._id)}
                            >
                              Reset Password
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {users.length === 0 && (
                    <Box p={8} textAlign="center">
                      <Text color="gray.500">No users found</Text>
                    </Box>
                  )}
                </Box>
              )}
            </TabPanel>

            {/* Sessions Tab Panel */}
            <TabPanel p={0} pt={4}>
              {isLoading ? (
                <Flex justify="center" p={8}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              ) : (
                <Box 
                  borderRadius="lg"
                  boxShadow="md"
                  bg={tableBgColor}
                  overflowX="auto"
                >
                  <Table variant="simple">
                    <Thead bg={headerBgColor}>
                      <Tr>
                        <Th>Session Name</Th>
                        <Th>Character</Th>
                        <Th>User</Th>
                        <Th>Last Updated</Th>
                        <Th>Messages</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sessions.map(session => (
                        <Tr key={session._id}>
                          <Td>{session.name}</Td>
                          <Td>
                            {session.characterId?.character_profile?.character_name || 'N/A'}
                          </Td>
                          <Td>
                            <Text isTruncated maxW="150px">
                              {session.userId?.username || session.userId?._id || 'N/A'}
                            </Text>
                          </Td>
                          <Td>{formatDate(session.updatedAt)}</Td>
                          <Td>{session.messageCount ?? session.messages?.length ?? 0}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Tooltip label="View Session">
                                <IconButton
                                  size="sm"
                                  colorScheme="green"
                                  icon={<ViewIcon />}
                                  aria-label="View Session"
                                  as={RouterLink}
                                  to={`/view-session/${session._id}`}
                                />
                              </Tooltip>
                              <Tooltip label="Delete Session">
                                <IconButton
                                  size="sm"
                                  colorScheme="red"
                                  icon={<DeleteIcon />}
                                  aria-label="Delete Session"
                                  onClick={() => handleDeleteSession(session._id)}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {sessions.length === 0 && (
                    <Box p={8} textAlign="center">
                      <Text color="gray.500">No sessions found</Text>
                    </Box>
                  )}
                </Box>
              )}
            </TabPanel>

            {/* AI Model Tab Panel */}
            <TabPanel p={0} pt={4}>
              {isLoading ? (
                <Flex justify="center" p={8}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              ) : (
                <Box
                  borderRadius="lg"
                  boxShadow="md"
                  bg={tableBgColor}
                  p={6}
                >
                  <VStack align="stretch" spacing={5} maxW="lg">
                    <Box>
                      <Heading size="md" mb={1}>Backend chat model</Heading>
                      <Text color="gray.500" fontSize="sm">
                        Models are published live by the LiteLLM router. The
                        selection applies to roleplay chat across all replicas
                        within ~15 seconds — no redeploy needed.
                      </Text>
                    </Box>

                    <HStack>
                      <Text minW="90px" fontWeight="medium">Current:</Text>
                      <Badge colorScheme="green" fontSize="0.9em">
                        {activeModel || 'unknown'}
                      </Badge>
                      {defaultModel && activeModel === defaultModel && (
                        <Tooltip label="No override set — using LITELLM_DEFAULT_MODEL">
                          <Badge colorScheme="gray">default</Badge>
                        </Tooltip>
                      )}
                    </HStack>

                    <Box>
                      <Text mb={2} fontWeight="medium">Switch model</Text>
                      <Select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        maxW="md"
                      >
                        {models.length === 0 && (
                          <option value="">No models available</option>
                        )}
                        {models.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </Select>
                      <Text mt={2} fontSize="xs" color="gray.500">
                        {models.length} model{models.length === 1 ? '' : 's'} available from the router
                      </Text>
                    </Box>

                    <HStack>
                      <Button
                        colorScheme="brand"
                        onClick={saveModel}
                        isLoading={savingModel}
                        isDisabled={!selectedModel || selectedModel === activeModel}
                      >
                        Save
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
        
        {/* Password Reset Modal */}
        {isPasswordResetOpen && (
          <Portal>
            <Box
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="blackAlpha.600"
              zIndex="modal"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                bg="white"
                borderRadius="lg"
                boxShadow="xl"
                p={6}
                maxW="md"
                w="full"
                mx={4}
              >
                <HStack justify="space-between" mb={4}>
                  <Heading size="md">Reset Token Issued</Heading>
                  <CloseButton onClick={onClosePasswordReset} />
                </HStack>

                <VStack spacing={4} align="stretch">
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Token created for {resetPasswordResult?.username}</AlertTitle>
                      <AlertDescription>
                        Share this with the user via a secure channel. They'll use it to set a new password.
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text mb={2}>Reset token (expires {resetPasswordResult?.expiresAt ? new Date(resetPasswordResult.expiresAt).toLocaleString() : 'in 24h'}):</Text>
                    <Text fontWeight="bold" fontSize="sm" fontFamily="monospace" wordBreak="break-all">
                      {resetPasswordResult?.resetToken}
                    </Text>
                  </Box>

                  <Text mt={4} color="red.500" fontWeight="medium">
                    Important: Single-use, displayed once. Do not paste into logs, tickets, or chat.
                  </Text>
                  
                  <HStack justify="flex-end">
                    <Button colorScheme="brand" onClick={onClosePasswordReset}>
                      Close
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          </Portal>
        )}
        
        {/* Delete Confirmation Modal */}
        {isDeleteConfirmOpen && (
          <Portal>
            <Box
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="blackAlpha.600"
              zIndex="modal"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={onCloseDeleteConfirm}
            >
              <Box
                bg="white"
                borderRadius="lg"
                boxShadow="xl"
                p={6}
                maxW="md"
                w="full"
                mx={4}
                onClick={(e) => e.stopPropagation()}
              >
                <HStack justify="space-between" mb={4}>
                  <Heading size="md">Confirm Deletion</Heading>
                  <CloseButton onClick={onCloseDeleteConfirm} />
                </HStack>
                
                <VStack spacing={4} align="stretch">
                  <Text>Are you sure you want to delete this session? This action cannot be undone.</Text>
                  
                  <HStack justify="flex-end" spacing={3}>
                    <Button variant="outline" onClick={onCloseDeleteConfirm}>
                      Cancel
                    </Button>
                    <Button colorScheme="red" onClick={confirmDeleteSession}>
                      Delete
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          </Portal>
        )}
      </Container>
    </Box>
  );
}

export default Admin;
